import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AdminLayout, Input, RichTextEditor, ImageCropper } from '../../components';
import api from '../../services/api';
import { formatDateTime, toInputDateTime } from '../../utils/dateUtils';
import SEO from '../../components/common/SEO';

const STEPS = [
  { key: 'basic', label: 'Basic Info', icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { key: 'details', label: 'Details', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { key: 'pricing', label: 'Pricing & Media', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { key: 'classes', label: 'Classes', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
];

const SplitSaveButton = ({ loading, isEditMode, onSavePublish, onSaveDraft }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        onClick={onSavePublish}
        disabled={loading}
        className="btn-primary px-5 py-2 text-sm font-medium rounded-r-none disabled:opacity-50"
      >
        {loading ? 'Saving...' : isEditMode ? 'Save & Publish' : 'Create & Publish'}
      </button>
      <button
        onClick={() => setOpen(!open)}
        disabled={loading}
        className="btn-primary px-2 py-2 text-sm rounded-l-none border-l border-primary-400 disabled:opacity-50"
      >
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-dark-200 z-20 py-1">
          <button
            onClick={() => { onSaveDraft(); setOpen(false); }}
            className="w-full text-left px-4 py-2.5 text-sm text-dark-700 hover:bg-dark-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Save as Draft
          </button>
        </div>
      )}
    </div>
  );
};

const CreateCourse = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [activeStep, setActiveStep] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [formData, setFormData] = useState({
    title: '', shortDescription: '', aboutCourse: '', whatYouWillLearn: '',
    prerequisites: '', category: '', level: 'Beginner', price: '',
    originalPrice: '', thumbnail: '', status: 'draft',
  });

  const [classes, setClasses] = useState([]);
  const [classForm, setClassForm] = useState({
    title: '', description: '', scheduledDate: '', duration: 60,
    meetingLink: '', status: 'draft', teacher: '',
  });
  const [editingClassId, setEditingClassId] = useState(null);
  const [showClassForm, setShowClassForm] = useState(false);
  const [classLoading, setClassLoading] = useState(false);

  const availableSteps = isEditMode ? STEPS : STEPS.filter(s => s.key !== 'classes');
  const currentIdx = availableSteps.findIndex(s => s.key === activeStep);
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx < availableSteps.length - 1;

  useEffect(() => {
    fetchTeachers();
    if (isEditMode && id) { fetchCourseData(); fetchClasses(); }
  }, [id]);

  const fetchCourseData = async () => {
    if (!id) return;
    try {
      setLoadingCourse(true); setError('');
      const response = await api.getCourse(id);
      const course = response.course || response;
      if (!course) throw new Error('Course data not found');
      setFormData({
        title: course.title || '', shortDescription: course.shortDescription || '',
        aboutCourse: course.aboutCourse || course.description || '',
        whatYouWillLearn: course.whatYouWillLearn || '', prerequisites: course.prerequisites || '',
        category: course.category || '', level: course.level || 'Beginner',
        price: course.price != null ? course.price.toString() : '',
        originalPrice: course.originalPrice != null ? course.originalPrice.toString() : '',
        thumbnail: course.thumbnail || '',
        status: course.status || 'draft',
      });
    } catch (err) { setError(err.message || 'Failed to load course data.'); }
    finally { setLoadingCourse(false); }
  };

  const fetchClasses = async () => {
    if (!id) return;
    try { const data = await api.getClassesByCourse(id); setClasses(data.classes || []); }
    catch (err) { console.error('Error fetching classes:', err); }
  };

  const fetchTeachers = async () => {
    try { const data = await api.getTeachers(); setTeachers(data.teachers || []); }
    catch (err) { console.error('Error fetching teachers:', err); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const buildCourseData = (statusOverride) => ({
    title: formData.title.trim(),
    shortDescription: formData.shortDescription?.trim() || '',
    description: formData.shortDescription?.trim() || formData.title,
    aboutCourse: formData.aboutCourse, whatYouWillLearn: formData.whatYouWillLearn,
    prerequisites: formData.prerequisites, category: formData.category,
    level: formData.level, price: parseFloat(formData.price) || 0,
    originalPrice: parseFloat(formData.originalPrice) || 0,
    thumbnail: formData.thumbnail || '',
    status: statusOverride || formData.status || 'draft',
  });

  const handleSave = async (statusOverride) => {
    setError(''); setSuccessMsg('');
    if (!formData.title.trim()) { setError('Course title is required'); setActiveStep('basic'); return; }
    if (!formData.category) { setError('Category is required'); setActiveStep('basic'); return; }
    setLoading(true);
    try {
      const courseData = buildCourseData(statusOverride);
      if (isEditMode) {
        await api.updateCourse(id, courseData);
        setSuccessMsg('Course saved successfully!');
        setFormData(prev => ({ ...prev, status: courseData.status }));
      } else {
        const result = await api.createCourse(courseData);
        setSuccessMsg('Course created successfully!');
        const newId = result.course?._id || result._id;
        if (newId) navigate(`/admin/courses/edit/${newId}`);
        else navigate('/admin/courses');
      }
    } catch (err) { setError(err.message || 'Failed to save course.'); }
    finally { setLoading(false); }
  };

  // Class CRUD
  const handleClassSubmit = async () => {
    if (!classForm.title.trim() || !classForm.scheduledDate) {
      setError('Class title and scheduled date are required'); return;
    }
    setClassLoading(true); setError('');
    try {
      if (editingClassId) await api.updateClass(editingClassId, classForm);
      else await api.createClass({ ...classForm, courseId: id });
      resetClassForm(); fetchClasses();
    } catch (err) { setError(err.message || 'Failed to save class'); }
    finally { setClassLoading(false); }
  };

  const handleEditClass = (cls) => {
    setEditingClassId(cls._id);
    const teacherId = cls.teacher ? (typeof cls.teacher === 'object' ? cls.teacher._id : cls.teacher) : '';
    setClassForm({
      title: cls.title, description: cls.description || '',
      scheduledDate: cls.scheduledDate ? toInputDateTime(cls.scheduledDate) : '',
      duration: cls.duration || 60, meetingLink: cls.meetingLink || '',
      status: cls.status || 'draft', teacher: teacherId,
    });
    setShowClassForm(true);
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Delete this class?')) return;
    try { await api.deleteClass(classId); fetchClasses(); }
    catch (err) { alert(err.message || 'Failed to delete class'); }
  };

  const handleToggleClassPublish = async (classId) => {
    try { await api.publishClass(classId); fetchClasses(); }
    catch (err) { alert(err.message || 'Failed to toggle class status'); }
  };

  const resetClassForm = () => {
    setEditingClassId(null);
    setClassForm({ title: '', description: '', scheduledDate: '', duration: 60, meetingLink: '', status: 'draft', teacher: '' });
    setShowClassForm(false);
  };

  if (isEditMode && loadingCourse) {
    return (
      <AdminLayout>
        <div className="max-w-5xl mx-auto p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
            <p className="mt-4 text-dark-500">Loading course data...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <SEO title={isEditMode ? `Edit Course` : 'Create Course'} noIndex />
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-dark-500 mb-6">
        <Link to="/admin/courses" className="hover:text-primary-500">Courses</Link>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        <span className="text-dark-900 font-medium">{isEditMode ? formData.title || 'Edit Course' : 'Create Course'}</span>
        <span className={`ml-3 text-xs font-medium px-2.5 py-0.5 rounded-full ${formData.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
          {formData.status === 'published' ? 'Published' : 'Draft'}
        </span>
      </nav>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 ml-4 text-lg leading-none">&times;</button>
        </div>
      )}
      {successMsg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm flex items-center justify-between">
          <span>{successMsg}</span>
          <button onClick={() => setSuccessMsg('')} className="text-green-400 hover:text-green-600 ml-4 text-lg leading-none">&times;</button>
          </div>
      )}

      <div className="flex gap-8">
        {/* Vertical Sidebar Nav */}
        <div className="w-52 flex-shrink-0 hidden md:block">
          <div className="sticky top-24">
            <nav className="space-y-1">
              {availableSteps.map((s, i) => {
                const isActive = activeStep === s.key;
                const isPast = i < currentIdx;
                return (
                  <button
                    key={s.key}
                    onClick={() => setActiveStep(s.key)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all text-left group ${
                      isActive
                        ? 'bg-primary-50 text-primary-700 shadow-sm border border-primary-200'
                        : isPast
                          ? 'text-dark-700 hover:bg-dark-50'
                          : 'text-dark-500 hover:bg-dark-50 hover:text-dark-700'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                      isActive ? 'bg-primary-500 text-white' : isPast ? 'bg-green-100 text-green-600' : 'bg-dark-100 text-dark-400 group-hover:bg-dark-200'
                    }`}>
                      {isPast ? (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                      ) : (
                        i + 1
                    )}
                  </div>
                    {s.label}
                  </button>
                );
              })}
            </nav>
          </div>
                </div>

        {/* Mobile step tabs */}
        <div className="md:hidden w-full mb-4">
          <div className="flex gap-1 overflow-x-auto pb-2">
            {availableSteps.map((s, i) => (
              <button key={s.key} onClick={() => setActiveStep(s.key)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium ${activeStep === s.key ? 'bg-primary-500 text-white' : 'bg-dark-100 text-dark-600'}`}>
                {s.label}
              </button>
              ))}
            </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Step Navigation Bar */}
          <div className="flex items-center justify-between mb-5">
            {hasPrev ? (
              <button
                onClick={() => setActiveStep(availableSteps[currentIdx - 1].key)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-dark-600 border border-dark-200 rounded-lg hover:bg-dark-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                Back
              </button>
            ) : <div />}

            <div className="flex items-center gap-3">
              {hasNext ? (
                <button
                  onClick={() => setActiveStep(availableSteps[currentIdx + 1].key)}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium btn-primary rounded-lg"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              ) : (
                <SplitSaveButton
                  loading={loading}
                  isEditMode={isEditMode}
                  onSavePublish={() => handleSave('published')}
                  onSaveDraft={() => handleSave('draft')}
                />
              )}
            </div>
          </div>

          {/* Basic Info */}
          {activeStep === 'basic' && (
            <div className="card p-6 md:p-8 space-y-6">
              <div>
                <h2 className="text-xl font-bold text-dark-900">Basic Information</h2>
                <p className="text-sm text-dark-500 mt-1">Set up the core details of your course</p>
              </div>

              <Input label="Course Title" name="title" placeholder="e.g., Complete React.js Masterclass" value={formData.title} onChange={handleChange} required />

              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1.5">Short Description</label>
                <textarea name="shortDescription" placeholder="Brief description (max 300 characters)..." value={formData.shortDescription} onChange={handleChange} rows={3} maxLength={300} className="w-full px-4 py-3 border border-dark-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-colors" />
                <p className="text-xs text-dark-400 mt-1 text-right">{formData.shortDescription.length}/300</p>
                </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1.5">Category</label>
                  <select name="category" value={formData.category} onChange={handleChange} required className="w-full px-4 py-3 border border-dark-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 bg-white transition-colors">
                    <option value="">Select category</option>
                    <option value="Web Development">Web Development</option>
                    <option value="Mobile Development">Mobile Development</option>
                    <option value="Data Science">Data Science</option>
                    <option value="Machine Learning">Machine Learning</option>
                    <option value="AI">AI</option>
                    <option value="Database">Database</option>
                    <option value="DevOps">DevOps</option>
                    <option value="Programming">Programming</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1.5">Level</label>
                  <div className="flex gap-2">
                    {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                      <label key={level} className={`flex-1 p-3 border-2 rounded-lg cursor-pointer text-center transition-all ${
                        formData.level === level ? 'border-primary-500 bg-primary-50 shadow-sm' : 'border-dark-200 hover:border-dark-300'
                      }`}>
                        <input type="radio" name="level" value={level} checked={formData.level === level} onChange={handleChange} className="sr-only" />
                        <span className={`text-sm font-medium ${formData.level === level ? 'text-primary-700' : 'text-dark-600'}`}>{level}</span>
                      </label>
                    ))}
                  </div>
                  </div>
                </div>
              </div>
            )}

          {/* Details */}
          {activeStep === 'details' && (
            <div className="card p-6 md:p-8 space-y-6">
              <div>
                <h2 className="text-xl font-bold text-dark-900">Course Details</h2>
                <p className="text-sm text-dark-500 mt-1">Add rich content for your course description and curriculum</p>
                      </div>

              <RichTextEditor label="About This Course" value={formData.aboutCourse} onChange={(content) => setFormData(prev => ({ ...prev, aboutCourse: content }))} placeholder="Describe what this course is about..." height={200} />
              <RichTextEditor label="What You'll Learn" value={formData.whatYouWillLearn} onChange={(content) => setFormData(prev => ({ ...prev, whatYouWillLearn: content }))} placeholder="List the learning objectives..." height={250} />
              <RichTextEditor label="Prerequisites" value={formData.prerequisites} onChange={(content) => setFormData(prev => ({ ...prev, prerequisites: content }))} placeholder="List the prerequisites..." height={150} />
                  </div>
          )}

          {/* Pricing & Media */}
          {activeStep === 'pricing' && (
            <div className="card p-6 md:p-8 space-y-6">
              <div>
                <h2 className="text-xl font-bold text-dark-900">Pricing & Media</h2>
                <p className="text-sm text-dark-500 mt-1">Set pricing and add visual assets for your course</p>
                </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Course Price ($)" type="number" name="price" placeholder="49.99" value={formData.price} onChange={handleChange} helperText="Set to 0 for a free course" />
                <Input label="Original Price ($)" type="number" name="originalPrice" placeholder="99.99" value={formData.originalPrice} onChange={handleChange} helperText="Shown as crossed-out price" />
                      </div>

              <ImageCropper
                label="Course Thumbnail"
                value={formData.thumbnail}
                onChange={(val) => setFormData(prev => ({ ...prev, thumbnail: val }))}
              />
              </div>
            )}

          {/* Classes */}
          {activeStep === 'classes' && isEditMode && (
            <div className="card p-6 md:p-8 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-dark-900">Manage Classes</h2>
                  <p className="text-sm text-dark-500 mt-1">Schedule class sessions for this course</p>
                </div>
                <button onClick={() => { resetClassForm(); setShowClassForm(true); }} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                  Add Class
                </button>
              </div>

              {showClassForm && (
                <div className="border border-primary-200 rounded-lg p-5 bg-primary-50/30 space-y-4">
                  <h3 className="font-semibold text-dark-900">{editingClassId ? 'Edit Class' : 'New Class'}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Class Title" value={classForm.title} onChange={(e) => setClassForm(prev => ({ ...prev, title: e.target.value }))} placeholder="e.g., Introduction to React" required />
                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1.5">Assign Teacher</label>
                      <select value={classForm.teacher} onChange={(e) => setClassForm(prev => ({ ...prev, teacher: e.target.value }))} className="w-full px-4 py-3 border border-dark-200 rounded-lg focus:outline-none focus:border-primary-500 bg-white">
                        <option value="">Select a teacher</option>
                        {teachers.map((t) => (
                          <option key={t.id || t._id} value={t.id || t._id}>{t.name} ({t.email})</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <RichTextEditor label="Description" value={classForm.description} onChange={(content) => setClassForm(prev => ({ ...prev, description: content }))} placeholder="Describe the class content..." height={120} />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1.5">Scheduled Date & Time</label>
                      <input type="datetime-local" value={classForm.scheduledDate} onChange={(e) => setClassForm(prev => ({ ...prev, scheduledDate: e.target.value }))} className="w-full px-4 py-3 border border-dark-200 rounded-lg focus:outline-none focus:border-primary-500" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1.5">Duration (min)</label>
                      <input type="number" value={classForm.duration} onChange={(e) => setClassForm(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))} className="w-full px-4 py-3 border border-dark-200 rounded-lg focus:outline-none focus:border-primary-500" min="1" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1.5">Status</label>
                      <select value={classForm.status} onChange={(e) => setClassForm(prev => ({ ...prev, status: e.target.value }))} className="w-full px-4 py-3 border border-dark-200 rounded-lg focus:outline-none focus:border-primary-500 bg-white">
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>

                  <Input label="Live Class Link" value={classForm.meetingLink} onChange={(e) => setClassForm(prev => ({ ...prev, meetingLink: e.target.value }))} placeholder="https://meet.google.com/abc-defg-hij" helperText="Google Meet, Zoom, Microsoft Teams, or any video conferencing link" />

                  <div className="flex gap-3 pt-2">
                    <button onClick={handleClassSubmit} disabled={classLoading} className="btn-primary px-5 py-2 text-sm disabled:opacity-50">
                      {classLoading ? 'Saving...' : editingClassId ? 'Update Class' : 'Add Class'}
                    </button>
                    <button onClick={resetClassForm} className="px-4 py-2 text-sm border border-dark-200 rounded-lg hover:bg-dark-50 transition-colors">Cancel</button>
                    </div>
                </div>
              )}

              {classes.length === 0 && !showClassForm ? (
                <div className="text-center py-12 text-dark-400">
                  <svg className="w-12 h-12 mx-auto mb-3 text-dark-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  <p className="font-medium text-dark-500">No classes yet</p>
                  <p className="text-sm mt-1">Click "Add Class" to schedule sessions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {classes.map((cls) => {
                    const teacherName = cls.teacher ? (typeof cls.teacher === 'object' ? cls.teacher.name : '') : '';
                    return (
                      <div key={cls._id} className="border border-dark-200 rounded-lg p-4 hover:border-dark-300 transition-colors flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-dark-900">{cls.title}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${cls.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {cls.status}
                            </span>
                          </div>
                          <p className="text-sm text-dark-500 mt-1">
                            {cls.scheduledDate ? formatDateTime(cls.scheduledDate) : 'No date set'}
                            {cls.duration ? ` · ${cls.duration} min` : ''}
                            {teacherName ? ` · ${teacherName}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button onClick={() => handleToggleClassPublish(cls._id)} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${cls.status === 'published' ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                            {cls.status === 'published' ? 'Unpublish' : 'Publish'}
                          </button>
                          <button onClick={() => handleEditClass(cls)} className="p-1.5 text-dark-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg" title="Edit">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => handleDeleteClass(cls._id)} className="p-1.5 text-dark-400 hover:text-red-500 hover:bg-red-50 rounded-lg" title="Delete">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeStep === 'classes' && !isEditMode && (
            <div className="card p-6 md:p-8 text-center">
              <svg className="w-12 h-12 mx-auto mb-3 text-dark-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              <p className="font-medium text-dark-500">Save the course first</p>
              <p className="text-sm text-dark-400 mt-1">You can add classes after creating the course</p>
            </div>
          )}

          </div>
        </div>
    </AdminLayout>
  );
};

export default CreateCourse;
